import { forwardRef } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/utils";
import { FieldShell } from "./FieldShell";

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  label?: ReactNode;
  /** Optional leading glyph rendered inside the field. */
  icon?: LucideIcon;
  error?: string | null;
  hint?: ReactNode;
  /** Rendered to the right of the label. */
  action?: ReactNode;
  /** Rendered inside the field, after the input (e.g. a spinner or unit). */
  trailing?: ReactNode;
  containerClassName?: string;
  onChange?: (value: string) => void;
}

/**
 * Glass text input with the electric focus glow. The whole control lives in an
 * `.auth-input` shell so focus-within lights the cyan ring exactly like the
 * auth pages — usable standalone, no `.glass-form` ancestor required.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      icon: Icon,
      error,
      hint,
      action,
      trailing,
      required,
      id,
      className,
      containerClassName,
      onChange,
      ...props
    },
    ref,
  ) => {
    return (
      <FieldShell
        id={id}
        label={label}
        required={required}
        error={error}
        hint={hint}
        action={action}
        className={containerClassName}
      >
        <div
          className={cn(
            "auth-input flex h-11 items-center gap-2.5 rounded-xl px-3.5",
            error && "!border-destructive/60",
            props.disabled && "opacity-60",
          )}
        >
          {Icon && (
            <Icon
              className="h-4 w-4 shrink-0"
              style={{ color: "rgba(var(--acc2-rgb),0.7)" }}
              aria-hidden="true"
            />
          )}
          <input
            ref={ref}
            id={id}
            required={required}
            aria-invalid={error ? true : undefined}
            onChange={(e) => onChange?.(e.target.value)}
            className={cn(
              "min-w-0 flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-[color:var(--landing-text-dim)]",
              className,
            )}
            style={{ color: "var(--landing-text)" }}
            {...props}
          />
          {trailing}
        </div>
      </FieldShell>
    );
  },
);
TextField.displayName = "TextField";
